/**
 * Shared Clerk `appearance` config — applied via `<ClerkProvider appearance>`
 * in [LocalizedClerkProvider](./components/LocalizedClerkProvider.tsx) so every
 * Clerk widget (`<SignIn/>`, `<SignUp/>`, `<UserButton/>`, `<UserProfile/>`,
 * `<OrganizationProfile/>`, `<OrganizationSwitcher/>`, `<PricingTable/>`)
 * picks up the eehae visual language without per-mount overrides.
 *
 * Strategy:
 *   - `variables` carry the global token mapping. Values reference the CSS
 *     custom properties defined in `styles/tokens.css` (`--ink`,
 *     `--paper-card`, `--violet-*`, `--r-*`, font tokens) so theme switches
 *     via the `[data-theme="dark"]` attribute on `<html>` flow through
 *     automatically.
 *   - `elements` overrides target the most-visible Clerk DOM hooks: the
 *     widget card, header text, primary CTA, form inputs, social buttons,
 *     dividers, footer link. Heavier per-component theming (org members
 *     table, pricing cards, etc.) is layered in later PRs as the redesign
 *     expands.
 *   - `layout` puts the logo inside the card and pushes social buttons to
 *     the top — matches the design's split-screen mockup.
 *
 * No icon URL is set on `layout.logoImageUrl`. Clerk falls back to the
 * dashboard-configured logo, which is the right behavior until the design
 * lands a finalized logo asset.
 *
 * Color refs use `var(--name)` strings rather than literal hex so the same
 * config drives both the light paper-cream theme and the dark variant
 * defined under `[data-theme="dark"]` in `styles/themes.css`.
 */
import type { Appearance } from '@clerk/types'

export const clerkAppearance: Appearance = {
  variables: {
    // Brand + semantic colors
    colorPrimary: 'var(--ink)',
    colorPrimaryForeground: '#ffffff',
    colorBackground: 'var(--paper-card)',
    colorForeground: 'var(--ink)',
    colorMutedForeground: 'var(--ink-muted)',
    colorMuted: 'var(--paper-soft)',
    colorBorder: 'var(--paper-line)',
    colorInput: 'var(--paper-card)',
    colorInputForeground: 'var(--ink)',
    colorRing: 'var(--violet-100)',
    colorDanger: 'var(--bad)',
    colorSuccess: 'var(--ok)',
    colorWarning: 'var(--warn)',

    // Type — Pretendard for body, Outfit for buttons (display font)
    fontFamily:
      "Pretendard, Inter, system-ui, -apple-system, 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif",
    fontFamilyButtons: "Outfit, system-ui, sans-serif",
    fontSize: '14px',

    // Radii — Clerk derives sm/lg/xl from this base
    borderRadius: 'var(--r-md)',
  },

  elements: {
    // Outer card — paper-cream surface, soft border, generous radius.
    // Default Clerk card has a heavy shadow; replace with the calmer
    // shadow used by the rest of the eehae UI.
    card: {
      backgroundColor: 'var(--paper-card)',
      borderColor: 'var(--paper-line)',
      borderRadius: 'var(--r-lg)',
      boxShadow: '0 4px 24px rgba(28, 21, 48, 0.04)',
    },

    // Header text — display font + ink, subtitle in muted ink
    headerTitle: {
      fontFamily: 'var(--font-display)',
      fontWeight: 600,
      letterSpacing: '-0.01em',
      color: 'var(--ink)',
    },
    headerSubtitle: {
      color: 'var(--ink-muted)',
    },

    // Primary CTA — match `.btn-primary` in styles/base.css: ink fill, pill
    // radius, body font weight 500. Disable Clerk's default
    // text-transform so labels stay sentence-case.
    formButtonPrimary: {
      backgroundColor: 'var(--ink)',
      color: '#ffffff',
      borderRadius: 'var(--r-pill)',
      fontFamily: 'var(--font-body)',
      fontWeight: 500,
      textTransform: 'none',
      letterSpacing: 0,
      '&:hover': {
        backgroundColor: 'var(--ink-2)',
      },
      '&:focus': {
        boxShadow: '0 0 0 3px var(--violet-100)',
      },
    },

    // Form fields — paper-card background, paper-line border, violet
    // focus ring matching the rest of the app's input style.
    formFieldInput: {
      backgroundColor: 'var(--paper-card)',
      borderColor: 'var(--paper-line-2)',
      borderRadius: 'var(--r-sm)',
      color: 'var(--ink)',
      '&:focus': {
        borderColor: 'var(--violet-400)',
        boxShadow: '0 0 0 3px var(--violet-100)',
      },
    },
    formFieldLabel: {
      color: 'var(--ink)',
      fontWeight: 500,
    },

    // Social SSO buttons — secondary-button vocabulary (transparent fill,
    // paper-line border, ink-2 text).
    socialButtonsBlockButton: {
      backgroundColor: 'transparent',
      borderColor: 'var(--paper-line-2)',
      borderRadius: 'var(--r-pill)',
      color: 'var(--ink-2)',
      fontWeight: 500,
      '&:hover': {
        backgroundColor: 'var(--paper-soft)',
      },
    },

    // Divider — soft paper-line + muted ink for the "or" label
    dividerLine: {
      backgroundColor: 'var(--paper-line)',
    },
    dividerText: {
      color: 'var(--ink-muted)',
    },

    // Footer "Sign up" / "Sign in" cross-link — violet to match the
    // brand-accent color used on other links throughout the app.
    footerActionLink: {
      color: 'var(--violet-700)',
      fontWeight: 500,
      '&:hover': {
        color: 'var(--violet-600)',
      },
    },

    // UserProfile / OrganizationProfile section titles — display font
    profileSectionTitleText: {
      fontFamily: 'var(--font-display)',
      color: 'var(--ink)',
    },
  },

  layout: {
    // Logo inside the card so the split-screen layout's left panel can
    // own the dominant brand mark; the in-card logo stays small and
    // referential.
    logoPlacement: 'inside',
    socialButtonsPlacement: 'top',
    socialButtonsVariant: 'blockButton',
    animations: true,
    privacyPageUrl: '/privacy',
    // No separate terms page yet; reuse the privacy URL so Clerk's
    // footer link doesn't 404.
    termsPageUrl: '/privacy',
  },
}
